import { useState, useEffect, useMemo } from "react";
import { differenceInSeconds } from "date-fns";

interface CountdownResult {
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
  isExpired: boolean;
  formatted: string;
  /** Ausgeschriebene Variante, z.B. "15 Tagen, 11 Stunden und 14 Minuten". */
  formattedLong: string;
}

/**
 * Schreibt eine Restzeit in Sekunden aus.
 *
 * @param kasus  'dativ' (Vorgabe) für „in 15 Tagen …",
 *               'nominativ' für „Noch 15 Tage …".
 */
export function formatCountdownLong(
  totalSeconds: number,
  kasus: "dativ" | "nominativ" = "dativ",
): string {
  if (totalSeconds <= 0) return "";

  const months = Math.floor(totalSeconds / (30 * 24 * 3600));
  const days = Math.floor((totalSeconds % (30 * 24 * 3600)) / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const tage = kasus === "dativ" ? "Tagen" : "Tage";
  const monate = kasus === "dativ" ? "Monaten" : "Monate";

  const parts: string[] = [];
  if (months > 0) parts.push(`${months} ${months === 1 ? "Monat" : monate}`);
  if (days > 0) parts.push(`${days} ${days === 1 ? "Tag" : tage}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "Stunde" : "Stunden"}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "Minute" : "Minuten"}`);
  if (seconds > 0 && months === 0 && days === 0 && hours === 0) {
    parts.push(`${seconds} ${seconds === 1 ? "Sekunde" : "Sekunden"}`);
  }
  return joinGerman(parts);
}

/** Fügt Teile im Deutschen zusammen: "a, b und c". */
function joinGerman(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} und ${parts[parts.length - 1]}`;
}

export function useCountdown(targetDate: Date | string | null): CountdownResult {
  const target = useMemo(() => {
    if (!targetDate) return null;
    return typeof targetDate === "string" ? new Date(targetDate) : targetDate;
  }, [targetDate]);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!target) return;

    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [target]);

  return useMemo(() => {
    if (!target) {
      return {
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isExpired: true,
        formatted: "",
        formattedLong: "",
      };
    }

    const totalSeconds = differenceInSeconds(target, now);

    if (totalSeconds <= 0) {
      return {
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isExpired: true,
        formatted: "",
        formattedLong: "",
      };
    }

    // Calculate time units
    const secondsInMinute = 60;
    const secondsInHour = 60 * 60;
    const secondsInDay = 24 * 60 * 60;
    const secondsInMonth = 30 * 24 * 60 * 60; // Approximate

    const months = Math.floor(totalSeconds / secondsInMonth);
    const remainingAfterMonths = totalSeconds % secondsInMonth;
    
    const days = Math.floor(remainingAfterMonths / secondsInDay);
    const remainingAfterDays = remainingAfterMonths % secondsInDay;
    
    const hours = Math.floor(remainingAfterDays / secondsInHour);
    const remainingAfterHours = remainingAfterDays % secondsInHour;
    
    const minutes = Math.floor(remainingAfterHours / secondsInMinute);
    const seconds = remainingAfterHours % secondsInMinute;

    // Create formatted string
    const parts: string[] = [];
    if (months > 0) parts.push(`${months} ${months === 1 ? "Monat" : "Monate"}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? "Tag" : "Tage"}`);
    if (hours > 0) parts.push(`${hours} Std`);
    if (minutes > 0) parts.push(`${minutes} Min`);
    if (seconds > 0 && months === 0 && days === 0) parts.push(`${seconds} Sek`);

    const formattedLong = formatCountdownLong(totalSeconds);


    return {
      months,
      days,
      hours,
      minutes,
      seconds,
      totalSeconds,
      isExpired: false,
      formatted: parts.join(" "),
      formattedLong,
    };
  }, [target, now]);
}
